<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import api from '@/services/axios';
import TabulatorRemote from '@/components/common/TabulatorRemote.vue';
import createMonitoringColumns from "@/columns/monitoring.js";
import PieChart from '@/components/charts/PieChart.vue';

const props = defineProps({
  publication: { type: Object, required: true }
});

const columns = computed(() =>
  createMonitoringColumns(resetAccess, props.publication.mode)
);

const groupName = ref(null);
const groupSize = ref(null);
const connectedCount = ref(null);
const submittedCount = ref(0);
const abandonedCount = ref(0);
const sessionList = ref([]);
const tableRef = ref(null);
const daysLeft = computed(() => {
  if (!props.publication.endDate) return "N/A";
  const today = new Date();
  const endDate = new Date(props.publication.endDate);
  const diffTime = endDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
});
const resetMessage = ref(null);
const connected = computed(() => connectedCount.value ?? 0);
const submitted = computed(() => submittedCount.value ?? 0);
const abandoned = computed(() => abandonedCount.value ?? 0);
const total = computed(() => groupSize.value ?? 1);

// URL dynamique pour TabulatorRemote
const apiUrl = computed(() => `/publications/${props.publication._id}/student-sessions`);
const refreshFlag = ref(0);
const isLoading = ref(false);

function formatDate(dateString) {
  return dateString ? dateString.split("T")[0] : "N/A";
}

// Fonction utilitaire pour vérifier les valeurs booléennes qui peuvent être stockées de différentes façons
function isTruthy(value) {
  return value === true || value === 1 || value === "true";
}

async function resetAccess(sessionId) {
  try {
    await api.patch(`/sessions/${sessionId}`);
    // Recharger toutes les données pour avoir les informations à jour
    await loadPublicationData();
    resetMessage.value = { type: "success", text: "Accès réinitialisé avec succès." };
    setTimeout(() => resetMessage.value = null, 3000);

  } catch (err) {
    resetMessage.value = { type: "danger", text: "Erreur lors de la réinitialisation de l'accès." };
    setTimeout(() => resetMessage.value = null, 4000);
    console.error("❌ Erreur resetAccess :", err);
  }
}

defineExpose({
  resetAccess
});


// 🔄 Fonction pour charger les données de la publication
async function loadPublicationData() {
  const groupId = props.publication.groupId?._id || props.publication.groupId;
  if (!groupId) {
    console.warn("❌ Aucun groupId trouvé pour la publication:", props.publication);
    return;
  }

  console.log("🔄 Chargement des données pour la publication:", props.publication._id);
  isLoading.value = true;

  try {
    // 🔹 Récupérer le nom du groupe
    const groupInfoRes = await api.get(`/groups/${groupId}`);
    groupName.value = groupInfoRes.data.groupName;

    // 🔹 Récupérer les étudiants du groupe
    const groupRes = await api.get(`/groups/${groupId}/students`);
    groupSize.value = groupRes.data.length;

    // 🔹 Récupérer les sessions de la publication
    const sessionRes = await api.get(`/publications/${props.publication._id}/student-sessions`);
    const sessions = sessionRes.data;
    console.log("🧪 Sessions reçues :", sessions);

    const mode = props.publication.mode;

    const enrichedSessions = sessions.map(session => ({
      ...session,
      publicationMode: mode,
    }));

    // Mettre à jour la liste des sessions
    sessionList.value = enrichedSessions;
    console.log("📝 sessionList mis à jour avec", enrichedSessions.length, "sessions");

    const studentStatus = new Map(); // Map studentId -> { submitted, abandoned, connected }

    sessions.forEach(session => {
      const studentId = session.studentID;
      if (!studentId) return;

      const existing = studentStatus.get(studentId) || {
        connected: false,
        submitted: false,
        abandoned: false,
      };

      // Vérification plus robuste des valeurs booléennes
      const isSubmitted = isTruthy(session.submitted) || isTruthy(session.accomplished);
      const isAbandoned = isTruthy(session.abandoned);

      studentStatus.set(studentId, {
        connected: true,
        submitted: existing.submitted || isSubmitted,
        abandoned: existing.abandoned || isAbandoned
      });
    });

    // 🔹 Calculs finaux
    connectedCount.value = studentStatus.size;
    submittedCount.value = [...studentStatus.values()].filter(s => s.submitted).length;
    abandonedCount.value = [...studentStatus.values()].filter(s => s.abandoned).length;

    console.log("📊 Stats calculées :", {
      total: groupSize.value,
      connected: connectedCount.value,
      submitted: submittedCount.value,
      abandoned: abandonedCount.value
    });

    // 🔄 Forcer la mise à jour du tableau en incrementant le refreshFlag
    refreshFlag.value++;
    console.log("🔄 RefreshFlag incrementé à", refreshFlag.value);
    console.log("📊 sessionList final contient", sessionList.value.length, "sessions:");
    console.log("📝 Échantillon des données sessionList:", sessionList.value.slice(0, 2));

  } catch (error) {
    console.error("❌ Erreur lors de la récupération des données :", error);
    groupSize.value = connectedCount.value = submittedCount.value = abandonedCount.value = "Erreur";
  } finally {
    isLoading.value = false;
  }
}

// 🔄 Surveiller les changements de publication
watch(
  () => props.publication,
  async (newPublication, oldPublication) => {
    if (newPublication && newPublication._id !== oldPublication?._id) {
      console.log("🔄 Nouvelle publication détectée, rechargement des données...", newPublication._id);
      await loadPublicationData();
    }
  },
  { deep: true, immediate: false }
);

onMounted(async () => {
  await loadPublicationData();
});
</script>

<template>
  <div>
    <h2>
      Publication Monitor : {{ publication.publicationName }}
      <span v-if="isLoading" class="spinner-border spinner-border-sm ms-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </span>
    </h2>

    <div class="row">
      <div class="col">
        <i>Publication ID: {{ publication._id }}</i>
      </div>
      <div class="col">
        👥 Group : <b>{{ groupName ?? '...' }}</b>
      </div>
      <div class="row">
        <div class="col">
          <div v-if="isLoading" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading charts...</span>
            </div>
            <p class="mt-2 text-muted">Loading publication data...</p>
          </div>
          <div v-else-if="groupSize !== null" class="charts">
            <div>
              <PieChart :connected="connected" :total="total" title="Connected Students" />
            </div>
            <div>
              <PieChart :connected="submitted" :total="total" title="Submited answers" />
            </div>
            <div>
              <PieChart :connected="abandoned" :total="total" title="Abandon" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row timeInfo MonitorPubMode">
      <div class="col-2">
        Mode : {{ publication.mode }}
      </div>
      <div class="col">Starting from: {{ formatDate(publication.startingDate) }}</div>
      <div class="col">End date: {{ formatDate(publication.endDate) }}</div>
      <div class="col">Days left: {{ daysLeft }}</div>
    </div>

    <hr />

    <h3>Student Session Statistics</h3>
    <div v-if="submittedCount > 0 || abandonedCount > 0" class="summary mb-3">
      <div class="alert alert-info">
        Total Sessions: <b>{{ connectedCount }}</b> |
        Submitted: <b>{{ submittedCount }}</b> |
        Abandoned: <b>{{ abandonedCount }}</b>
      </div>
    </div>
    <div v-if="resetMessage" class="alert" :class="`alert-${resetMessage.type}`">
      {{ resetMessage.text }}
    </div>
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading sessions...</span>
      </div>
      <p class="mt-2 text-muted">Loading session data...</p>
    </div>
    <TabulatorRemote
      v-else
      :key="`monitor-${props.publication._id}`"
      ref="tableRef"
      :data="sessionList"
      :columns="columns"
      :refresh-flag="refreshFlag"
      tableType="monitoring"
    />
  </div>
</template>

<style scoped>
.MonitorPubMode {
  text-align: center;
  border: 1px grey solid;
  background-color: rgb(239, 250, 247);
  border-radius: 5px;
}

.charts {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.charts>div {
  width: 250px;
}
</style>